// @flow
import React, {
  useState,
  useCallback,
  useRef,
  useMemo,
  useEffect
} from "react";
import { createPortal } from "react-dom";
import dayjs from "dayjs";
import todayTomorrowDateFormat from "@justpark/helpers/src/todayTomorrowDateFormat/todayTomorrowDateFormat";
import Typography from "../Typography";
import BasicButton from "../BasicButton";
import DatePickerWithTimeDropDown from "../DatePickerWithTimeDropDown";
import ConditionalWrapper from "../ConditionalWrapper";
import useModalHook from "../../hooks/useModalHook";
import useMatchMediaMatches from "../../hooks/useMatchMediaMatches";
import useSynchronisedState from "../../hooks/useSynchronisedState";
import useOutsideClickHandler from "../../hooks/useOutsideClickHandler";
import SearchFormFullscreenHeader from "../SearchFormFullscreenHeader";

import styles from "./searchFormDateInput.module.scss";

type Props = {
  date: string,
  setDate: (date: string) => void,
  type: "from" | "to" | "monthly",
  blockPastDates?: boolean,
  min?: string | null,
  max?: string | null,
  invalid?: boolean,
  setDefaultDates: any
};

const cypress = "search-form-date-input";

const getLabel = (type) => {
  switch (type) {
    case "to":
      return "Until";
    case "monthly":
      return "Starting on";
    default:
      return "From";
  }
};

const getFullscreenHeader = (type) => {
  switch (type) {
    case "to":
      return "Park until";
    default:
      return "Park from";
  }
};

const SearchFormDateInput = ({
  date,
  setDate,
  type,
  blockPastDates = true,
  min = null,
  max = null,
  invalid = false,
  setDefaultDates
}: Props) => {
  const ref = useRef();
  const [syncDate, setSyncDate] = useSynchronisedState(date);
  const [pickerOpen, setPickerOpen] = useState(false);
  const isMobileLayout = useMatchMediaMatches("(max-width: 639px)");

  const minimumDate = useMemo(() => {
    if (min) {
      return dayjs(min).format("YYYY-MM-DD");
    }

    if (blockPastDates) {
      return dayjs().format("YYYY-MM-DD");
    }

    return null;
  }, [min, blockPastDates]);

  const onDateChange = useCallback(
    (value) => {
      setSyncDate(value);
    },
    [setSyncDate]
  );

  const onDateClose = useCallback(() => {
    setPickerOpen(false);
    setDate(syncDate);
  }, [setDate, syncDate, setPickerOpen]);

  const showInWrapper = pickerOpen && isMobileLayout;
  useModalHook(showInWrapper, "modal-open");

  useOutsideClickHandler(ref, onDateClose, pickerOpen);

  const alignClass = type === "to" ? styles.alignRight : styles.alignLeft;

  const label = getLabel(type);
  const fullscreenHeader = getFullscreenHeader(type);
  const timeText = type === "to" ? "Exit before" : "Enter after";

  const onCancel = useCallback(() => {
    setPickerOpen(false);
    setSyncDate(date);
  }, [setSyncDate, date, setPickerOpen]);

  useEffect(() => {
    if (pickerOpen && !date) {
      setSyncDate(setDefaultDates());
    }
  }, [date, pickerOpen, setDefaultDates, setSyncDate]);

  return (
    <div
      className={`${alignClass} ${styles.base}`}
      data-cy={`${cypress}-${type}`}
    >
      <BasicButton
        className={styles.triggerButton}
        onClick={() => {
          setPickerOpen(true);
        }}
      >
        <Typography
          color={invalid ? "error" : "brand"}
          className={styles.label}
        >
          {label}
        </Typography>

        <Typography className={styles.dateText}>
          {syncDate
            ? todayTomorrowDateFormat(syncDate, false, type !== "monthly")
            : "Select date and time"}
        </Typography>
      </BasicButton>

      {pickerOpen && (
        <ConditionalWrapper
          condition={showInWrapper}
          wrapper={(children) => {
            return createPortal(children, document.getElementById("portal"));
          }}
        >
          <div
            ref={ref}
            className={
              showInWrapper
                ? styles.fullscreenModal
                : styles.datePickerContainer
            }
          >
            {showInWrapper && (
              <SearchFormFullscreenHeader
                header={fullscreenHeader}
                onCancel={onCancel}
              />
            )}

            {pickerOpen && !isMobileLayout && (
              <button
                type="button"
                className={styles.datePickerOverlay}
                onClick={onDateClose}
              >
                Close date picker
              </button>
            )}

            <div className={styles.datePicker}>
              <DatePickerWithTimeDropDown
                isFullscreen={showInWrapper}
                onDateChange={onDateChange}
                date={syncDate}
                showTimePicker={type !== "monthly"}
                onClose={onDateClose}
                buttonText="Done"
                blockDates={() => false}
                timeText={timeText}
                maxDate={max}
                minDate={minimumDate}
              />
            </div>
          </div>
        </ConditionalWrapper>
      )}
    </div>
  );
};

export default SearchFormDateInput;
